import React, { useEffect, useState, useCallback } from 'react';
import {
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, ResponsiveContainer
} from 'recharts';
import {
  Box, Typography, Snackbar, Alert as MuiAlert
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AiOutlineProject } from "react-icons/ai";
import { LuIndianRupee } from "react-icons/lu";
import { FiUsers } from "react-icons/fi";
import { BsGlobeCentralSouthAsia } from "react-icons/bs";
import { getAuth } from 'firebase/auth';
import { collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import './HomePage.css';

const HomePage = () => {
  const [firstName, setFirstName] = useState('User');
  const [organizationId, setOrganizationId] = useState('');
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const TABL_ORG_ID = "cQmEW4C2qwMyulOYs921";

  const [dynamicStats, setDynamicStats] = useState({
    projects: 0,
    budget: 0,
    activeUsers: 0,
    locations: 0,
  });

  // Fetch logged-in user info
  useEffect(() => {
    const fetchUser = async () => {
      const authUser = getAuth().currentUser;
      if (authUser?.email) {
        const snap = await getDocs(
          query(collection(db, 'users'), where('email', '==', authUser.email))
        );
        if (!snap.empty) {
          const data = snap.docs[0].data();
          setFirstName(data.firstName || 'User');
          setOrganizationId(data.organizationId || '');
        }
      }
    };
    fetchUser();
  }, []);

  // Fetch projects & stats for non-TABL
  useEffect(() => {
    const fetchData = async () => {
      if (organizationId && organizationId !== TABL_ORG_ID) {
        const projSnap = await getDocs(
          query(collection(db, 'projects'), where('organizationId', '==', organizationId))
        );
        const userSnap = await getDocs(
          query(collection(db, 'users'), where('organizationId', '==', organizationId))
        );

        const projectsData = projSnap.docs.map(doc => doc.data());
        const budgetSum = projectsData.reduce((sum, p) => sum + Number(p.budget || 0), 0); 

        setProjects(projectsData);
        setDynamicStats({
          projects: projectsData.length,
          budget: budgetSum,
          activeUsers: userSnap.size,
          locations: new Set(projectsData.flatMap(p => p.states || [])).size,
        });
      }
    };
    fetchData();
  }, [organizationId]);

  // Listen to users
  useEffect(() => {
    if (!organizationId) return;
    const q = query(collection(db, "users"), where("organizationId", "==", organizationId));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((doc, idx) => {
          const d = doc.data();
          return {
            id: doc.id,
            sno: idx + 1,
            firstName: d.firstName || "",
            lastName: d.lastName || "",
            email: d.email || "",
            designation: d.designation || "",
          };
        });
        setUsers(list);
      },
      (error) => {
        console.error(error);
        setSnackbar({ open: true, message: "Failed to fetch users." });
      }
    );
    return () => unsubscribe();
  }, [organizationId]);

  // Aggregate SDG & sector data
  const getChartData = useCallback(() => {
    const sdgMap = {}, sectorMap = {};
    projects.forEach(p => {
      (p.sdgs || []).forEach(sdg => { sdgMap[sdg] = (sdgMap[sdg] || 0) + 1; });
      (p.sectors || []).forEach(sec => { sectorMap[sec] = (sectorMap[sec] || 0) + 1; });
    });
    return {
      sdgData: Object.entries(sdgMap).map(([name, value]) => ({ name, value })),
      sectorData: Object.entries(sectorMap).map(([name, value]) => ({ name, value })),
    };
  }, [projects]);

  const { sdgData, sectorData } = getChartData();
  const COLORS = ['#6A5ACD', '#FF7F50', '#32CD32', '#FFD700', '#20B2AA', '#e5243b', '#26bde2', '#fcc30b'];

  const userColumns = [
    { field: "sno", headerName: "S.No.", width: 60 },
    { field: "firstName", headerName: "First Name", flex: 1 },
    { field: "lastName", headerName: "Last Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.5 },
    { field: "designation", headerName: "Designation", flex: 1 },
  ];

  return (
    <Box className="Home-container" p={3}>
      <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Lato, sans-serif', color: '#645792', fontWeight: 'bold' }}>
        Welcome to your Home Page, {firstName}!
      </Typography>

      <div className="stats-container">
        <StatCard title="Projects" value={organizationId === TABL_ORG_ID ? "1250+" : dynamicStats.projects} icon={<AiOutlineProject />} />
        <StatCard title="Total Budget" value={organizationId === TABL_ORG_ID ? "200 Cr" : dynamicStats.budget.toFixed(1)} icon={<LuIndianRupee />} />
        <StatCard title="Active Users" value={organizationId === TABL_ORG_ID ? "340+" : dynamicStats.activeUsers} icon={<FiUsers />} />
        <StatCard title="States" value={organizationId === TABL_ORG_ID ? "18+" : dynamicStats.locations} icon={<BsGlobeCentralSouthAsia />} />
      </div>

      <div className="bottom-table">
        <div className="sdgs-container">
          <h3>SDGs</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={organizationId === TABL_ORG_ID ? staticSDGs : sdgData}>
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <RechartsTooltip content={<BarTooltip />} />
              <Bar dataKey="value">
                {(organizationId === TABL_ORG_ID ? staticSDGs : sdgData).map((e, idx) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="content-row">
        <div className="sectors-container">
          <h3>Sectors</h3>
          <div className="pie-and-legend">
            <PieChart width={250} height={250}>
              <Pie
                data={organizationId === TABL_ORG_ID ? staticSectors : sectorData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={85}
                dataKey="value"
                labelLine={false}
              >
                {(organizationId === TABL_ORG_ID ? staticSectors : sectorData).map((e, idx) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip content={<CustomTooltip />} />
            </PieChart>
            <div className="legend-container">
              {(organizationId === TABL_ORG_ID ? staticSectors : sectorData).map((item, idx) => (
                <div key={idx} className="legend-item">
                  <span className="legend-color" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                  <span className="legend-text">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Box flex={1} ml={2}>
          <h3>Users</h3>
          <DataGrid
            rows={users.map((u, idx) => ({ ...u, sno: idx + 1 }))}
            columns={userColumns}
            autoHeight
            disableRowSelectionOnClick
            sx={{
              borderRadius: 2,
              "& .MuiDataGrid-columnHeader": { backgroundColor: "rgba(180, 87, 146,0.1)" },
              "& .MuiDataGrid-row": { borderBottom: "1px solid rgba(0,0,0,0.1)" },
            }}
          />
        </Box>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setSnackbar({ ...snackbar, open: false })} severity="info" sx={{ width: "100%" }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <footer className="footer"><p>Designed By Tabl.</p></footer>
    </Box>
  );
};

const StatCard = ({ title, value, icon }) => (
  <div className="stat-card">
    <div className="stat-content">
      <h3>{title}</h3>
      <h1>{value}</h1>
    </div>
    <div className="stat-icon">{icon}</div>
  </div>
);

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const { name, value } = payload[0];
    return (
      <div className="recharts-tooltip">
        <strong>{name}</strong>: {value}
      </div>
    );
  }
  return null;
};

const BarTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const { name, value } = payload[0].payload;
    return (
      <div className="recharts-tooltip">
        <strong>{name}</strong>: {value}
      </div>
    );
  }
  return null;
};

const staticSectors = [
  { name: 'Education', value: 25 },
  { name: 'Livelihood', value: 20 },
  { name: 'Gender', value: 15 },
  { name: 'Health', value: 20 },
  { name: 'Environment', value: 20 },
];

const staticSDGs = [
  { sdg: 'SDG 1', name: 'No Poverty', value: 40, color: '#e5243b' },
  { sdg: 'SDG 2', name: 'Zero Hunger', value: 55, color: '#dda63a' },
  { sdg: 'SDG 3', name: 'Good Health and Well-being', value: 70, color: '#4c9f38' },
  { sdg: 'SDG 4', name: 'Quality Education', value: 68, color: '#c5192d' },
  { sdg: 'SDG 5', name: 'Gender Equality', value: 95, color: '#ff3a21' },
  { sdg: 'SDG 6', name: 'Clean Water and Sanitation', value: 90, color: '#26bde2' },
  { sdg: 'SDG 7', name: 'Affordable and Clean Energy', value: 85, color: '#fcc30b' },
  { sdg: 'SDG 9', name: 'Industry, Innovation and Infrastructure', value: 30, color: '#f26a2e' },
  { sdg: 'SDG 10', name: 'Reduced Inequalities', value: 45, color: '#e01483' },
  { sdg: 'SDG 11', name: 'Sustainable Cities and Communities', value: 60, color: '#f89d2a' },
  { sdg: 'SDG 12', name: 'Responsible Consumption and Production', value: 78, color: '#bf8b2e' },
  { sdg: 'SDG 13', name: 'Climate Action', value: 80, color: '#407f46' },
  { sdg: 'SDG 14', name: 'Life Below Water', value: 85, color: '#1f97d4' },
  { sdg: 'SDG 15', name: 'Life on Land', value: 45, color: '#56c02b' },
];

export default HomePage;
